import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader, KPICard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Gift, Plus, CheckCircle2, IndianRupee, User as UserIcon, Award } from "lucide-react";
import { INR } from "@/lib/constants";
import { toast } from "sonner";
import ReferDialog from "@/components/ReferDialog";

const STATUS_COLOR = { PENDING: "#A1A1AA", CONTACTED: "#7DD3FC", CONVERTED: "#00E676", LOST: "#FF3B30" };
const TYPE_COLOR = { MEMBER: "#CCFF00", COACH: "#FF6B35", ADMIN: "#7DD3FC" };

export default function ReferralsPage() {
  const { user, activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [convertOpen, setConvertOpen] = useState(null); // referral being converted
  const [revenueInput, setRevenueInput] = useState("");
  const params = activeBranchId ? { branch_id: activeBranchId } : {};

  const { data: refs = [] } = useQuery({
    queryKey: ["referrals", activeBranchId],
    queryFn: async () => (await apiClient.get("/referrals", { params })).data,
  });
  const { data: leaders = [] } = useQuery({
    queryKey: ["referrals-leaderboard", activeBranchId],
    queryFn: async () => (await apiClient.get("/referrals/leaderboard", { params })).data,
  });

  const convert = useMutation({
    mutationFn: async ({ id, body }) => (await apiClient.patch(`/referrals/${id}`, body)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["referrals"] });
      qc.invalidateQueries({ queryKey: ["referrals-leaderboard"] });
      const reward = d.referrer_type === "COACH"
        ? `₹${d.cash_bonus_inr} cash bonus credited`
        : `${d.reward_points} loyalty points credited`;
      toast.success(`Marked converted — ${reward}`);
      setConvertOpen(null);
      setRevenueInput("");
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => (await apiClient.patch(`/referrals/${id}`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referrals"] }); toast.success("Status updated"); },
  });

  const converted = refs.filter((r) => r.status === "CONVERTED");
  const revenue = converted.reduce((s, r) => s + (r.converted_revenue || 0), 0);
  const points = refs.reduce((s, r) => s + (r.reward_points || 0), 0);

  const memberRefs = refs.filter((r) => r.referrer_type === "MEMBER");
  const coachRefs = refs.filter((r) => r.referrer_type === "COACH");

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="referrals-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Loyalty</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Referral Engine</h1>
          <div className="text-zinc-500 text-sm mt-1">
            Members & Coaches earn REPS · earn rates & redemption catalog managed in Rewards Settings.
          </div>
        </div>
        <ReferDialog trigger={
          <Button data-testid="admin-refer-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider">
            <Plus size={16} className="mr-1" /> Add Referral
          </Button>
        } />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Referrals" value={refs.length} testId="kpi-ref-total" />
        <KPICard label="Converted" value={converted.length} testId="kpi-ref-converted" />
        <KPICard label="Revenue" value={INR(revenue)} testId="kpi-ref-revenue" />
        <KPICard label="REPS Issued" value={points} testId="kpi-ref-points" sub="Total credited" />
      </div>

      <Tabs defaultValue="all">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-volt data-[state=active]:text-black">All</TabsTrigger>
          <TabsTrigger value="member" data-testid="tab-member" className="data-[state=active]:bg-volt data-[state=active]:text-black">By Members ({memberRefs.length})</TabsTrigger>
          <TabsTrigger value="coach" data-testid="tab-coach" className="data-[state=active]:bg-volt data-[state=active]:text-black">By Coaches ({coachRefs.length})</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="data-[state=active]:bg-volt data-[state=active]:text-black">
            <Award size={12} className="mr-1" /> Leaderboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4"><ReferralsTable refs={refs} onConvertClick={setConvertOpen} onStatusChange={(id, status) => updateStatus.mutate({ id, status })} /></TabsContent>
        <TabsContent value="member" className="mt-4"><ReferralsTable refs={memberRefs} onConvertClick={setConvertOpen} onStatusChange={(id, status) => updateStatus.mutate({ id, status })} /></TabsContent>
        <TabsContent value="coach" className="mt-4"><ReferralsTable refs={coachRefs} onConvertClick={setConvertOpen} onStatusChange={(id, status) => updateStatus.mutate({ id, status })} /></TabsContent>
        <TabsContent value="leaderboard" className="mt-4">
          <Panel>
            <PanelHeader subtitle="Top referrers" title="Leaderboard" />
            {leaders.length === 0 ? <div className="text-zinc-600 text-sm py-6 text-center">No referrals yet.</div> : (
              <div className="space-y-2">
                {leaders.map((l, i) => (
                  <div key={l.referrer_id} data-testid={`leader-${l.referrer_id}`} className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-900 rounded-sm">
                    <div className="w-8 text-center font-display text-2xl font-black text-zinc-700">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate flex items-center gap-2">
                        {l.name || "(unknown)"}
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm" style={{ background: `${TYPE_COLOR[l.referrer_type] || '#888'}22`, color: TYPE_COLOR[l.referrer_type] || '#888' }}>{l.referrer_type}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{l.converted}/{l.total} converted · {INR(l.revenue)} revenue</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl font-bold text-volt leading-none">{l.points || 0}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">REPS</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>

      {/* Conversion dialog */}
      <Dialog open={!!convertOpen} onOpenChange={(o) => !o && setConvertOpen(null)}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-sm" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Mark Converted</DialogTitle>
          </DialogHeader>
          {convertOpen && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-zinc-400">Referrer: <span className="text-white font-semibold">{convertOpen.referrer_name}</span> <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm ml-1" style={{ background: `${TYPE_COLOR[convertOpen.referrer_type] || '#888'}22`, color: TYPE_COLOR[convertOpen.referrer_type] || '#888' }}>{convertOpen.referrer_type}</span></div>
                <div className="text-zinc-400 mt-1">Referee: <span className="text-white font-semibold">{convertOpen.referral_name}</span></div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Plan value (₹)</Label>
                <Input data-testid="convert-revenue" type="number" value={revenueInput} onChange={(e) => setRevenueInput(e.target.value)}
                  placeholder="e.g. 7500" className="bg-zinc-950 border-zinc-800 mt-1" />
              </div>
              <div className="bg-volt/10 border border-volt/30 text-volt text-xs px-3 py-2 rounded-sm">
                {convertOpen.referrer_type === "COACH"
                  ? `${convertOpen.referrer_name} will be credited ₹${Math.max(1000, Math.round((Number(revenueInput) || 0) * 0.10))} cash bonus.`
                  : `${convertOpen.referrer_name} will be credited ${Math.max(500, Math.round((Number(revenueInput) || 0) * 0.05))} loyalty points.`}
              </div>
              <Button onClick={() => convert.mutate({ id: convertOpen.id, body: { status: "CONVERTED", converted_revenue: Number(revenueInput) || 0 } })}
                disabled={convert.isPending} data-testid="confirm-convert"
                className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
                {convert.isPending ? "Crediting…" : "Confirm & Credit Reward"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReferralsTable({ refs, onConvertClick, onStatusChange }) {
  return (
    <Panel>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            <tr className="border-b border-zinc-900">
              <th className="text-left py-2 px-2 font-semibold">Referrer</th>
              <th className="text-left py-2 px-2 font-semibold">Referee</th>
              <th className="text-left py-2 px-2 font-semibold">Status</th>
              <th className="text-left py-2 px-2 font-semibold">Revenue</th>
              <th className="text-left py-2 px-2 font-semibold">Reward</th>
              <th className="text-right py-2 px-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {refs.map((r) => (
              <tr key={r.id} data-testid={`ref-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-950">
                <td className="py-3 px-2">
                  <div className="flex items-center gap-2">
                    <UserIcon size={12} className="text-zinc-500" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.referrer_name || "—"}</div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm"
                        style={{ background: `${TYPE_COLOR[r.referrer_type] || '#888'}22`, color: TYPE_COLOR[r.referrer_type] || '#888' }}>
                        {r.referrer_type}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="font-semibold">{r.referral_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-cmd">{r.referral_phone}</div>
                </td>
                <td className="py-3 px-2">
                  <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm"
                    style={{ background: `${STATUS_COLOR[r.status]}22`, color: STATUS_COLOR[r.status] }}>{r.status}</span>
                </td>
                <td className="py-3 px-2">{INR(r.converted_revenue)}</td>
                <td className="py-3 px-2">
                  <span className="text-volt font-display font-bold">{r.reward_points || 0} REPS</span>
                </td>
                <td className="py-3 px-2 text-right">
                  {r.status !== "CONVERTED" && r.status !== "LOST" && (
                    <div className="flex items-center justify-end gap-1">
                      {r.status === "PENDING" && (
                        <Button size="sm" variant="outline" onClick={() => onStatusChange(r.id, "CONTACTED")} data-testid={`contact-${r.id}`}
                          className="bg-zinc-900 border-zinc-800 text-blue-400 hover:border-blue-500 text-[10px] uppercase">Mark Contacted</Button>
                      )}
                      <Button size="sm" onClick={() => onConvertClick(r)} data-testid={`convert-${r.id}`}
                        className="bg-volt hover:bg-volt-hover text-black font-bold uppercase text-[10px]">
                        <CheckCircle2 size={10} className="mr-1" /> Convert
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onStatusChange(r.id, "LOST")} data-testid={`lost-${r.id}`}
                        className="bg-zinc-900 border-zinc-800 text-red-400 hover:border-red-500 text-[10px] uppercase">Lost</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {refs.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-zinc-600">No referrals</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
